<script setup lang="ts">
import { twMerge } from "tailwind-merge";
import { computed, inject, ref, shallowRef, watch } from "vue";
import { control } from "@/design/control";
import Avatar from "@/design/primitives/Avatar.vue";
import Modal from "@/design/primitives/Modal.vue";
import Popover from "@/design/primitives/Popover.vue";
import TextField from "@/design/primitives/TextField.vue";
import {
	LOOKUP_PORT,
	type LookupOption,
	RECORD_FORM,
	SUBFORM_PORT,
} from "@/shared/contracts/ports";
import type { EntityDefinition, RelationDefinition } from "@/shared/contracts/structure";
import { describeServerError } from "@/shared/i18n/describe-error";
import { say } from "../say";
import type { WidgetProps } from "./contract";

/**
 * A pointer to another record, a contact or a user.
 *
 * It searches rather than listing everything: an entity with four thousand rows
 * in a dropdown is unusable, and the day it happens is the day someone discovers
 * the select was loading all of them.
 */
const props = defineProps<WidgetProps & { controlId?: string; describedBy?: string }>();
const emit = defineEmits<{ "update:modelValue": [unknown]; blur: [] }>();

const RecordForm = inject(RECORD_FORM, null);
const lookup = inject(LOOKUP_PORT, null);
const structure = inject(SUBFORM_PORT, null);
const query = ref("");
const options = ref<LookupOption[]>([]);
const open = ref(false);
const currentLabel = ref("");
const failure = ref("");
/** A PESSOA QUE AINDA NÃO EXISTE (30/09): o seletor de contato ganha a porta de criar. */
const searched = ref(false);
const creating = ref(false);
const draft = ref({ name: "", phone: "", email: "" });
const createError = ref("");
const canCreate = computed(
	() =>
		entity.value === "contact" &&
		typeof lookup?.createContact === "function" &&
		searched.value &&
		query.value.trim().length > 0 &&
		options.value.length === 0 &&
		!failure.value,
);
const draftReady = computed(() =>
	Boolean(draft.value.name.trim() || draft.value.phone.trim() || draft.value.email.trim()),
);
function startCreating(): void {
	draft.value = { name: query.value.trim(), phone: "", email: "" };
	createError.value = "";
	creating.value = true;
}
async function createContact(): Promise<void> {
	if (!lookup?.createContact || !draftReady.value) return;
	const input = {
		...(draft.value.name.trim() ? { name: draft.value.name.trim() } : {}),
		...(draft.value.phone.trim() ? { phone: draft.value.phone.trim() } : {}),
		...(draft.value.email.trim() ? { email: draft.value.email.trim() } : {}),
	};
	try {
		const made = await lookup.createContact(input);
		creating.value = false;
		currentLabel.value = made.label;
		pick(made);
	} catch (e) {
		createError.value = describeServerError(e, say("lookupFailed"));
	}
}

const entity = computed(() => {
	if (props.field.type === "ref_contact") return "contact";
	if (props.field.type === "ref_user") return "app_user";
	return typeof props.field.config.entity === "string" ? props.field.config.entity : "";
});

/**
 * What to type into the box, in the words of the person typing.
 *
 * The core lists are named after their SQL views, and `buscar em app_user…` was a
 * database schema showing through onto a screen in Portuguese. An entity the
 * TENANT defined is named by its display NAME ("Matérias-primas"), read from the
 * structure — até 27/09 saía o código (`buscar em raw_material…`), inglês de
 * banco no meio de um formulário em português. Sem a estrutura à mão (a página
 * pública), o código continua de reserva.
 *
 * Um por fonte da plataforma, e não uma tradução genérica: são oito nomes fixos.
 * O que NÃO se traduz é o código de uma entidade do TENANT — aquele nome é dele, e
 * a tela não tem o que melhorar nele.
 *
 * Um `Set` e não um objeto: `entity` vem de `config.entity`, que alguém digita, e
 * num objeto comum `constructor` responde com uma FUNÇÃO — que o `??` não pega e
 * que apareceria como código-fonte dentro da caixa de busca. `Set.has` não tem
 * protótipo por onde escorregar. O servidor recusa o mesmo truque na whitelist
 * dele pela mesma razão.
 */
const CORE_NAMES = new Set([
	"app_user",
	"contact",
	"channel",
	"pipeline",
	"stage",
	"message_template",
	"artifact",
	"schedule",
]);

const prompt = computed(() => {
	// O comentário antigo dizia que este trecho era inglês "como todo padrão de
	// `dynamic/`", e as oito frases logo acima estavam em português — era a linha
	// que falava fora de tom num painel inglês, e ela mesma dizia que não estava.
	// Agora `dynamic/` TEM dicionário (`shared/i18n/dynamic-form.ts`) e a
	// pergunta some. O `placeholder` do campo continua a mandar quando a tela tem
	// coisa melhor a dizer.
	if (!entity.value) return "no entity configured";
	if (CORE_NAMES.has(entity.value)) return say(`lookup.${entity.value}`);
	// O nome de uma entidade do TENANT nunca passa pelo dicionário: é dele.
	return say("lookupIn", { entity: entityName.value || entity.value });
});

const entityName = ref("");
const targetSchema = shallowRef<EntityDefinition | null>(null);
const recordOpen = ref(false);
const recordRelations = ref<RelationDefinition[]>([]);
const recordLoading = ref(false);
const recordBusy = ref(false);
const recordError = ref("");
const canCreateRecord = computed(
	() =>
		!props.disabled &&
		!!RecordForm &&
		!!lookup?.createRecord &&
		!!targetSchema.value &&
		targetSchema.value.canWrite !== false,
);
async function startRecord(): Promise<void> {
	if (!canCreateRecord.value || recordLoading.value) return;
	const code = entity.value;
	open.value = false;
	recordError.value = "";
	recordLoading.value = true;
	try {
		const relations = (await structure?.relationsOf?.(code)) ?? [];
		if (entity.value !== code) return;
		recordRelations.value = relations;
		recordOpen.value = true;
	} catch (error) {
		failure.value = describeServerError(error, say("subformLoadFailed"));
	} finally {
		recordLoading.value = false;
	}
}
async function saveRecord(values: Record<string, unknown>): Promise<void> {
	if (!lookup?.createRecord || recordBusy.value || props.disabled) return;
	recordBusy.value = true;
	recordError.value = "";
	try {
		const made = await lookup.createRecord({ entity: entity.value, values });
		currentLabel.value = made.label;
		pick(made);
		recordOpen.value = false;
	} catch (error) {
		recordError.value = describeServerError(error, say("subformAddFailed"));
	} finally {
		recordBusy.value = false;
	}
}
watch(
	entity,
	async (code) => {
		entityName.value = "";
		targetSchema.value = null;
		recordOpen.value = false;
		if (!code || CORE_NAMES.has(code) || !structure) return;
		const found = await structure.entityOf(code).catch(() => null);
		if (entity.value === code) {
			entityName.value = found?.name ?? "";
			targetSchema.value = found;
		}
	},
	{ immediate: true },
);

const currentId = computed(() =>
	props.modelValue === null || props.modelValue === undefined ? "" : String(props.modelValue),
);

/** O que já foi escolhido não é um controle, mas ocupa o lugar de um — logo, a
    mesma moldura e a mesma altura, para a linha não pular ao trocar. */
const chosenBox = computed(() => twMerge(control(), "flex min-w-0 flex-1 items-center"));

// Shows a NAME for an existing value, not a uuid. A form that redisplays what
// someone chose as "8f2c…" makes them think it saved the wrong thing.
watch(
	[currentId, entity],
	async ([id, target]) => {
		if (!id || !target || !lookup) {
			currentLabel.value = "";
			return;
		}
		// Nunca o uuid na caixa: sem nome, a palavra neutra (o id continua no valor).
		currentLabel.value =
			(await lookup.label({ entity: target, id }).catch(() => null)) ?? say("optionSource.unnamed");
	},
	{ immediate: true },
);

let timer: ReturnType<typeof setTimeout> | null = null;
watch(query, (text) => {
	if (timer) clearTimeout(timer);
	// Debounced: a request per keystroke is a request per keystroke.
	timer = setTimeout(async () => {
		if (!lookup || !entity.value) return;
		searched.value = false;
		try {
			const found = await lookup.search({ entity: entity.value, query: text, limit: 12 });
			searched.value = true;
			// Quem já foi reivindicado (1:1) não é oferecido — melhor não aparecer do
			// que aparecer e ser recusado no salvar. O índice único continua sendo a
			// verdade; isto é só o seletor sendo honesto de antemão.
			options.value = props.taken ? found.filter((o) => !props.taken?.has(o.id)) : found;
			failure.value = "";
		} catch (e) {
			// O servidor recusa uma busca não liberada com a razão ESCRITA — "Este campo
			// não permite busca pública." Engolir o erro devolvia uma lista vazia, que é
			// exatamente a caixa vazia silenciosa que aquela rota foi escrita para acabar.
			options.value = [];
			failure.value = e instanceof Error ? e.message : say("lookupFailed");
		}
	}, 220);
});

function pick(option: LookupOption): void {
	emit("update:modelValue", option.id);
	open.value = false;
	query.value = "";
}
</script>

<template>
	<!--
		A lista sai por portal, e não é enfeite: este campo é desenhado dentro de um
		formulário dinâmico que pode estar dentro de um `SubformWidget`, dentro de um
		diálogo, dentro de um painel com `overflow-y-auto` — e um `absolute` era
		cortado em qualquer um deles. `anchor` porque quem abre é o foco no campo, e
		o foco tem de ficar onde está: uma lista que o roubasse pararia a digitação.
		A largura vem da largura do próprio campo, medida pela reka.
	-->
	<Popover
		:open="open && options.length > 0"
		anchor
		label="Sugestões"
		panel-class="max-h-56 w-[var(--reka-popper-anchor-width)] overflow-y-auto"
		@update:open="open = $event"
	>
		<template #trigger>
			<div v-if="currentId && !open" class="flex items-center gap-2">
				<span :class="chosenBox">
					<span class="truncate">{{ currentLabel || currentId }}</span>
				</span>
				<button
					type="button"
					class="text-xs text-fg-subtle underline"
					:disabled="disabled"
					@click="emit('update:modelValue', null)"
				>
					trocar
				</button>
			</div>

			<template v-else>
				<TextField
					:id="controlId"
					v-model="query"
					:disabled="disabled"
					:placeholder="prompt"
					:invalid="Boolean(error)"
					:described-by="describedBy"
					@focus="open = true"
					@blur="emit('blur')"
				/>
				<button v-if="canCreateRecord" :disabled="recordLoading" type="button" class="mt-1 min-h-9 text-xs text-accent underline" @click="startRecord">
					{{ say("lookup.createRecord", { entity: entityName }) }}
				</button>
				<p v-if="failure" class="mt-1 text-xs text-danger">{{ failure }}</p>
				<!-- Não achou ninguém e é um contato: a porta de criar, com telefone ou
				     e-mail — é o que faz a mesma pessoa continuar um registro só. -->
				<button
					v-if="canCreate && !creating"
					type="button"
					class="mt-1 text-xs text-accent underline"
					@click="startCreating"
				>
					{{ say("lookup.createContact", { name: query.trim() }) }}
				</button>
				<div v-if="creating" class="mt-2 space-y-2 rounded-xl border border-border p-2">
					<p class="text-xs text-fg-subtle">{{ say("lookup.createHint") }}</p>
					<TextField v-model="draft.name" :placeholder="prompt" size="sm" />
					<TextField v-model="draft.phone" type="tel" :placeholder="say('lookup.createPhone')" size="sm" />
					<TextField v-model="draft.email" type="email" :placeholder="say('lookup.createEmail')" size="sm" />
					<p v-if="createError" class="text-xs text-danger">{{ createError }}</p>
					<p v-else-if="!draftReady" class="text-xs text-fg-subtle">{{ say("lookup.createNeeds") }}</p>
					<div class="flex gap-2">
						<button type="button" class="text-xs text-accent underline" :disabled="!draftReady" @click="createContact">
							{{ say("lookup.createSave") }}
						</button>
						<button type="button" class="text-xs text-fg-subtle underline" @click="creating = false">
							{{ say("lookup.createCancel") }}
						</button>
					</div>
				</div>
			</template>
		</template>

		<!--
			A escolha é no `click`, e não no `mousedown`: o painel âncora já segura o
			foco no campo em qualquer aperto lá dentro (`Popover`), então o campo não
			perde o foco entre apertar e soltar. Escolher no `mousedown` desmontava a
			lista no meio do clique — o soltar caía no que estava por baixo — e deixava
			as opções mortas para o teclado, porque Enter e espaço num botão disparam
			`click`, nunca `mousedown`. O `p-1` tira as opções dos cantos arredondados,
			onde o recorte do painel fazia um aperto na ponta de um nome valer "fora".
		-->
		<ul class="p-1">
			<li v-for="option in options" :key="option.id">
				<button
					type="button"
					class="flex w-full items-center gap-2.5 px-3 py-2 text-left hover:bg-bg-muted rounded-lg"
					@click="pick(option)"
				>
					<!-- O rosto sai do id, como na caixa de entrada e no card do funil: a
					     mesma pessoa tem a mesma cor em toda tela, que é o que torna uma
					     lista de nomes varrível. -->
					<Avatar
						v-if="option.person"
						:name="option.label"
						:seed="option.id"
						size="xs"
						class="shrink-0"
					/>
					<span class="min-w-0 flex-1">
						<span class="block truncate text-sm text-fg">{{ option.label }}</span>
						<!-- A segunda linha é o que responde "qual Maria?". Colada ao rótulo
						     ela era a primeira metade a ser cortada. -->
						<span v-if="option.hint" class="block truncate text-xs text-fg-subtle">
							{{ option.hint }}
						</span>
					</span>
				</button>
			</li>
		</ul>
	</Popover>
	<Modal :open="recordOpen" :title="say('lookup.createRecord', { entity: entityName })" :close-label="say('cancel')" @update:open="!recordBusy && (recordOpen = $event)">
		<component :is="RecordForm" v-if="targetSchema && recordOpen" :entity="targetSchema" :relations="recordRelations.filter(r => r.kind === 'many_to_one')" :busy="recordBusy" :server-error="recordError" :submit-label="say('create')" @submit="saveRecord" @cancel="!recordBusy && (recordOpen = false)" />
	</Modal>
</template>
